import React, {useEffect, useState} from "react";
import {ProjectColors} from "../../../Enums/Core/projectColors";
import {Button, Col, Row, Spinner} from "react-bootstrap";
import DataMultiSelector from "../DataMultiSelector/DataMultiSelector";
import TimeScaleFilter from "../TimeScaleFilter/TimeScaleFilter";
import BarChart from "../../BarChart/BarChart";
import {
    DAYS, HOURS, MAX_SELECTION_LIMIT, MONTHS, WEEKS
} from "../../../Constants/constants";
import DrillDownModal from "../DrillDownModal/DrillDownModal";
import {apolloClientReports} from "../../../GraphQL/config";
import {QUERY_GET_LEAK_VOLUME_OVER_TIME} from "../../../GraphQL/ReportGenerator/queries";
import {
    getDailyOffsetEndDate,
    getDailyOffsetStartDate,
    getEndDate,
    getMonthlyEndDate,
    getMonthlyOffsetEndDate,
    getMonthlyStartDate,
    getMonthlyOffsetStartDate,
    getResolutionName,
    getWeeklyOffsetEndDate,
    getWeeklyOffsetStartDate,
    pushCsvDates,
    setCsvHeaders,
    gqlDateFormat,
    buildOverTimeCategoriesData,
    getPointResolution,
    setDisabledOffsetButtons
} from "../Utils/utils";
import LeakVolumeOverTimeHighResolution from "./LeakVolumeOverTimeHighResolution";
import {BsFillSkipEndFill, BsFillSkipStartFill} from "react-icons/bs";
import convert, {DEFAULT_VOLUME_UNIT} from "../MeasurementText/MeasurementTextConverter";

export default function LeakVolumeOverTime(props) {
    const BAR_CHART_HEIGHT = 345;
    const defaultScale = MONTHS;
    const [isLoading, setIsLoading] = useState(true);
    const [startDate, setStartDate] = useState(getMonthlyStartDate());
    const [endDate, setEndDate] = useState(getMonthlyEndDate());
    const [selectedDate, setSelectedDate] = useState();
    const [modalShow, setModalShow] = React.useState(false);
    const [barIndex, setBarIndex] = useState(defaultScale);
    const [resolution, setResolution] = useState(defaultScale);
    const [offset, setOffset] = useState(0);
    const [entities, setEntities] = useState();
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [categories, setCategories] = useState();
    const [clickable, setClickable] = useState(true);
    const colors = [ProjectColors.Pink, ProjectColors.SteelBlue, ProjectColors.DarkTeal, ProjectColors.Orange];
    const [chartData, setChartData] = useState();
    const [csvData, setCsvData] = useState([]);
    const [leftArrowDisabled, setLeftArrowDisabled] = useState(false);
    const [rightArrowDisabled, setRightArrowDisabled] = useState(true);
    const [volumeUnit, setVolumeUnit] = useState(DEFAULT_VOLUME_UNIT);

    const entityType = props.entityLabel;

    const getLeakVolumeOverTimeFromDB = () => {
        apolloClientReports.query({
            query: QUERY_GET_LEAK_VOLUME_OVER_TIME, variables: {
                resolution: getResolutionName(resolution),
                from: gqlDateFormat(startDate),
                to: gqlDateFormat(endDate),
                entityType: entityType
            }
        }).then(res => {
            let response = res?.data?.leakVolumeEntityHistogram;
            if (!response) {
                console.error(`Data from getLeakVolumeOverTimeFromDB is empty`);
            } else {
                setVolumeUnit(convert(response.measureUnit.volumeUnit));
                let sortedEntities = response?.entities?.slice().sort((a, b) => parseFloat(b.data[b.data.length - 1].volume) - parseFloat(a.data[a.data.length - 1].volume));
                setEntities(sortedEntities.map((entity) => entity));

                if (selectedEntities?.length === 0) {
                    setSelectedEntities(sortedEntities.map((entity) => entity).slice(0, MAX_SELECTION_LIMIT));
                } else {
                    let selectedEntitiesIds = selectedEntities?.map((entity) => entity.id);
                    let usedEntities = sortedEntities.map((entity) => {
                        if (selectedEntitiesIds.includes(entity.id)) {
                            return entity;
                        }
                    }).filter((entity) => entity !== undefined);
                    setSelectedEntities(usedEntities);
                }
                setDisabledOffsetButtons(response, offset, setRightArrowDisabled, setLeftArrowDisabled, startDate, resolution);
                setCsvData(buildCsvData(sortedEntities));
                setIsLoading(false);
            }
        })
    }

    const buildCsvData = (responseData) => {
        let csvDataLocal = [];
        let headers = setCsvHeaders(resolution);

        responseData?.forEach((entity) => {
            headers.push(`Leak volume - ${entity.name}`);
        });
        csvDataLocal.push(headers);

        responseData[0]?.data?.forEach((entityData, index) => {
            let row = [index + 1];
            row = pushCsvDates(resolution, entityData.date, row);
            csvDataLocal.push(row);
        })
        responseData.forEach((entity) => {
            entity.data.forEach((entityData, index) => {
                csvDataLocal[index + 1]?.push(entityData?.volume ? entityData.volume : 0);
            })
        })

        return csvDataLocal;
    }

    useEffect(() => {
        setIsLoading(true);
        getLeakVolumeOverTimeFromDB();
    }, [startDate, endDate]);

    useEffect(() => {
        switch (resolution) {
            case MONTHS:
                setStartDate(getMonthlyOffsetStartDate(offset));
                setEndDate(getMonthlyOffsetEndDate(offset));
                break;
            case WEEKS:
                setStartDate((getWeeklyOffsetStartDate(offset)));
                setEndDate((getWeeklyOffsetEndDate(offset)));
                break;
            case DAYS:
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
        }
    }, [offset]);

    useEffect(() => {
        let now = new Date();
        if (offset !== 0) setOffset(0);
        switch (resolution) {
            case HOURS:
                setStartDate(props.selectedDate || now);
                setEndDate(getEndDate(props.selectedDate, resolution));
                break;
            case DAYS:
                setClickable(false);
                setStartDate(getDailyOffsetStartDate(offset));
                setEndDate(getDailyOffsetEndDate(offset));
                break;
            case WEEKS:
                setClickable(true);
                setStartDate((getWeeklyOffsetStartDate(offset)));
                setEndDate((getWeeklyOffsetEndDate(offset)));
                break;
            default:
                setClickable(true);
                setStartDate(props.selectedDate || getMonthlyStartDate());
                setEndDate(now);
                break;
        }
    }, [resolution]);


    useEffect(() => {
        setCategories(buildOverTimeCategoriesData(selectedEntities, resolution));
    }, [selectedEntities]);

    useEffect(() => {
        setChartData(selectedEntities?.map((a, index) => {
            return {
                name: a.name, data: a.data?.map((dat) => dat.volume), color: colors[index]
            }
        }));
    }, [categories]);

    return (<div>
        <Row>
            <Col md={12}>
                <p className={'card-title-main bold-title'}>{props.title}</p>
            </Col>
            <Col md={4}>
                <DataMultiSelector
                    variant={props.variant}
                    setSelectedValues={setSelectedEntities}
                    selectionLimit={MAX_SELECTION_LIMIT}
                    entities={entities?.map((entity) => {
                        return {...entity, value: entity.value, label: entity.name}
                    })}
                    selectedValues={selectedEntities?.map((entity) => {
                        return {...entity, value: entity.value, label: entity.name}
                    })}/>
            </Col>
            <Col md={8}>
                <TimeScaleFilter
                    setOffSet={setOffset}
                    hasExportCsv
                    setScale={setResolution}
                    defaultScale={resolution}
                    className={'time-scale-filter-small-card'}
                    csvData={csvData} title={`Leak volume over time (compare ${entityType}`}/>
            </Col>
        </Row>
        <br/>
        {isLoading && <Spinner animation="border" variant="primary"/>}
        {!isLoading && <>
            <Row className={!props.isModal ? 'LeakVolumeOverTimeChart' : ''}>
                <Col md={1}><Button
                    className={'offset_button_small'}
                    disabled={leftArrowDisabled}
                    onClick={() => setOffset(offset - 1)}> <BsFillSkipStartFill size={30} color={'#686B73'}/>
                </Button>
                </Col>
                <Col md={10}>
                    {clickable ? <BarChart
                        volumeUnit={volumeUnit}
                        height={BAR_CHART_HEIGHT}
                        categories={categories}
                        data={chartData}
                        onClick={(event) => {
                            getPointResolution(event, resolution, setSelectedDate, setBarIndex, setModalShow)
                        }}
                    /> : <BarChart
                        volumeUnit={volumeUnit}
                        categories={categories}
                        data={chartData}
                    />}
                </Col>
                <Col md={1}><Button
                    className={'offset_button_small'}
                    disabled={rightArrowDisabled}
                    onClick={() => setOffset(offset + 1)}> <BsFillSkipEndFill size={30} color={'#686B73'}/>
                </Button>
                </Col>
            </Row>
            <DrillDownModal
                show={modalShow}
                onHide={() => setModalShow(false)}
                type={LeakVolumeOverTimeHighResolution}
                resolution={resolution}
                barIndex={barIndex}
                selectedEntities={props.selectedEntities || selectedEntities}
                variant={props.variant}
                selectedDate={selectedDate}
                colors={colors}
            />
        </>}
    </div>)
}
